/**
 * The one status -> colour mapping for the whole dashboard.
 *
 * Colour is the only thing carrying meaning in the stacked bars, so it has to
 * mean the same thing in every one of them: the overview bar, the per-row ratio
 * bar in the table (Task 22) and the drill-down time series (Task 23). A local
 * palette in each component would drift the moment one of them gained a bucket,
 * and a reader comparing a row against the overview would silently be comparing
 * two different legends.
 *
 * The buckets mirror `app.services.health.bucket_for` where it matters: 429 and
 * 402 are pulled out of the 4xx mass because on a paid API they are business
 * signals (rate limiting, quota exhausted), not client mistakes.
 *
 * Every class name below is written out in full. Tailwind scans source text, so
 * a computed `bg-s-${key}` would compile to nothing and every bar would render
 * transparent.
 */
import type { TranslationKey } from '../i18n'
import type { HealthState } from '../api/types'

export const STATUS_BUCKETS = ['2xx', '3xx', '4xx', '402', '429', '5xx'] as const
export type StatusBucket = (typeof STATUS_BUCKETS)[number]

/**
 * Colour, plus the one label that has no language.
 *
 * `label` stays because '2xx' and '429' are protocol tokens, identical in every
 * locale. The long form that used to sit beside it as `description` does NOT:
 * it is a sentence, it lives in `BUCKET_DESCRIPTION_KEYS` below, and leaving an
 * English copy on a map six components import is how the next person needing a
 * tooltip ships an English one that type-checks.
 */
export interface BucketStyle {
  /** Short legend label — a protocol token, never translated. */
  label: string
  /** Solid fill, for bar segments. */
  bg: string
  /** Foreground, for legend dots and text. */
  text: string
}

export const BUCKET_STYLES: Record<StatusBucket, BucketStyle> = {
  '2xx': { label: '2xx', bg: 'bg-s-2xx', text: 'text-s-2xx' },
  '3xx': { label: '3xx', bg: 'bg-s-3xx', text: 'text-s-3xx' },
  '4xx': { label: '4xx', bg: 'bg-s-4xx', text: 'text-s-4xx' },
  '402': { label: '402', bg: 'bg-s-402', text: 'text-s-402' },
  '429': { label: '429', bg: 'bg-s-429', text: 'text-s-429' },
  '5xx': { label: '5xx', bg: 'bg-s-5xx', text: 'text-s-5xx' },
}

/**
 * The same five colours again, this time as values rather than class names.
 *
 * ECharts paints onto a canvas, so it never sees a stylesheet: `bg-s-5xx` means
 * nothing to it and `hsl(var(--s-5xx))` would resolve to nothing either, because
 * custom properties are a CSS-cascade feature and canvas has no cascade. The
 * drill-down chart therefore has to be handed literal colours.
 *
 * Rather than duplicate the palette (which would drift the first time a theme
 * changed, and quietly make red mean one thing in the table and another in the
 * chart) the vars are read back off the document at render time, with the
 * light-theme values from `index.css` as the fallback for any environment that
 * has no stylesheet loaded — jsdom under test, above all.
 */
export const BUCKET_CSS_VARS: Record<StatusBucket, string> = {
  '2xx': '--s-2xx',
  '3xx': '--s-3xx',
  '4xx': '--s-4xx',
  '402': '--s-402',
  '429': '--s-429',
  '5xx': '--s-5xx',
}

/** Must stay in step with the `:root` block of `src/index.css`. */
export const BUCKET_FALLBACK_COLORS: Record<StatusBucket, string> = {
  '2xx': 'hsl(152 46% 40%)',
  '3xx': 'hsl(200 68% 42%)',
  '4xx': 'hsl(28 80% 50%)',
  '402': 'hsl(268 52% 54%)',
  '429': 'hsl(46 82% 46%)',
  '5xx': 'hsl(356 68% 50%)',
}

export type BucketPalette = Record<StatusBucket, string>

/**
 * Resolve the bucket palette to literal colours for canvas rendering.
 *
 * Reads the live custom properties so a dark-mode chart matches the dark-mode
 * bars; falls back per bucket, not all-or-nothing, so one missing variable
 * cannot silently repaint the other four.
 */
export function readBucketPalette(root?: Element | null): BucketPalette {
  const element = root ?? (typeof document === 'undefined' ? null : document.documentElement)
  const computed = element ? getComputedStyle(element) : null
  const palette = {} as BucketPalette
  for (const bucket of STATUS_BUCKETS) {
    const raw = computed?.getPropertyValue(BUCKET_CSS_VARS[bucket]).trim()
    palette[bucket] = raw ? `hsl(${raw})` : BUCKET_FALLBACK_COLORS[bucket]
  }
  return palette
}

/**
 * Read one `--token` from the theme as a literal colour.
 *
 * Canvas and SVG chart renderers cannot resolve CSS variables, so anything a
 * chart paints has to be resolved to a literal first — same reason
 * `readBucketPalette` exists. `fallback` covers SSR and jsdom, where there is
 * no computed style to read.
 */
export function readThemeColor(token: string, fallback: string, root?: Element | null): string {
  const element = root ?? (typeof document === 'undefined' ? null : document.documentElement)
  const raw = element ? getComputedStyle(element).getPropertyValue(token).trim() : ''
  return raw ? `hsl(${raw})` : fallback
}

/**
 * The status columns in the endpoint table.
 *
 * These are LITERAL codes, not the colour buckets above, and the two lists
 * exist for different jobs. A column headed `400` counts responses that were
 * exactly 400; `5xx` is the one class, because every server error is the same
 * story and individually they are rounding errors here (1,141 requests in 24h
 * against 10.2M).
 *
 * Codes with no column of their own — 202, 401, 403, 404, 405, and anything
 * 3xx — are not lost: they are still in the Mix bar, which classifies by the
 * colour buckets and therefore accounts for every request, and in the
 * drill-down pie, which breaks out every code individually.
 */
export const STATUS_COLUMNS = ['200', '400', '401', '402', '403', '404', '405', '429', '5xx'] as const
export type StatusColumn = (typeof STATUS_COLUMNS)[number]

/** Which colour bucket paints each column. */
export const COLUMN_BUCKET: Record<StatusColumn, StatusBucket> = {
  '200': '2xx',
  '400': '4xx',
  '401': '4xx',
  '402': '402',
  '403': '4xx',
  '404': '4xx',
  '405': '4xx',
  '429': '429',
  '5xx': '5xx',
}

/** The column a status code belongs in, or `null` when it has none. */
export function columnFor(status: number): StatusColumn | null {
  if (status >= 500) return '5xx'
  if (status === 429) return '429'
  if (status === 402) return '402'
  if (status === 400) return '400'
  if (status === 401) return '401'
  if (status === 403) return '403'
  if (status === 404) return '404'
  if (status === 405) return '405'
  if (status === 200) return '200'
  return null
}

/**
 * Per-column totals for one endpoint. Columns with no requests are omitted, so
 * a caller can tell "this column saw nothing" from "this endpoint was never
 * measured" — the distinction this whole dashboard turns on.
 */
export function columnTotals(byStatus: Record<string, number>): Partial<Record<StatusColumn, number>> {
  const out: Partial<Record<StatusColumn, number>> = {}
  for (const [code, n] of Object.entries(byStatus)) {
    const status = Number(code)
    if (!Number.isFinite(status) || n <= 0) continue
    const column = columnFor(status)
    if (column === null) continue
    out[column] = (out[column] ?? 0) + n
  }
  return out
}

export function bucketFor(status: number): StatusBucket {
  if (status >= 500) return '5xx'
  // 402 used to share the 429 bucket — the backend groups both as THROTTLED.
  // For colour that hid a real distinction: on this zone 402 is 14% of traffic
  // and 429 is 18%, and "quota exhausted" and "rate limited" are different
  // operational stories. The backend's health maths is unchanged; this is only
  // how the two are drawn.
  if (status === 402) return '402'
  if (status === 429) return '429'
  if (status >= 400) return '4xx'
  if (status >= 300) return '3xx'
  return '2xx'
}

/**
 * Fold a `by_status` map (JSON object keys are strings) into bucket totals.
 *
 * Returns every bucket that has at least one request, in canonical order, so
 * two bars drawn from different data still read left-to-right the same way.
 */
export function bucketTotals(byStatus: Record<string, number>): { bucket: StatusBucket; count: number }[] {
  const totals = new Map<StatusBucket, number>()
  for (const [code, n] of Object.entries(byStatus)) {
    const status = Number(code)
    if (!Number.isFinite(status) || n <= 0) continue
    const bucket = bucketFor(status)
    totals.set(bucket, (totals.get(bucket) ?? 0) + n)
  }
  return STATUS_BUCKETS.filter((b) => totals.has(b)).map((b) => ({
    bucket: b,
    count: totals.get(b) as number,
  }))
}

/**
 * Health-state COLOUR, shared with the table's badges.
 *
 * Colour only. The state's name and its explanatory sentence are words, so they
 * are keys — `HEALTH_LABEL_KEYS` and `HEALTH_HINT_KEYS` below — and this map
 * deliberately has no `label`/`hint` field for a call site to reach for by
 * accident. `HEALTH_STYLES[state].label` used to render 'Degraded' in both
 * languages; the type now says it does not exist.
 */
export const HEALTH_STYLES: Record<HealthState, { bg: string; text: string }> = {
  down: { bg: 'bg-down', text: 'text-down' },
  degraded: { bg: 'bg-degraded', text: 'text-degraded' },
  healthy: { bg: 'bg-healthy', text: 'text-healthy' },
  unknown: { bg: 'bg-unknown', text: 'text-unknown' },
}

/**
 * The translated half of the two style maps above.
 *
 * `BUCKET_STYLES` and `HEALTH_STYLES` above carry colour, which has no
 * language; the words they used to carry are keyed here instead. Keys rather than strings, so a language switch re-renders them;
 * the maps are shared for the same reason the colours are, so "degraded" reads
 * identically in the table badge, the family chip, the overview tile and the
 * status filter, and so no second set of Chinese for the same five buckets can
 * drift in beside the first.
 *
 * The keys themselves are `dashboard.statusBucket.*` / `dashboard.healthHint.*`
 * — the ones `Overview` and `StatusFilter` already reach for by template
 * literal. Spelling them out as maps buys what a template literal cannot: the
 * compiler checks them against `TranslationKey`, so a bucket added to
 * `STATUS_BUCKETS` without a string is a build failure rather than a `2xx`
 * rendering as its own key name.
 *
 * Bucket LABELS (`2xx`, `429`) are deliberately absent: they are protocol
 * tokens and are the same in every language, so `BUCKET_STYLES[b].label` is
 * still the thing to render.
 */
export const BUCKET_DESCRIPTION_KEYS: Record<StatusBucket, TranslationKey> = {
  '2xx': 'dashboard.statusBucket.2xx',
  '3xx': 'dashboard.statusBucket.3xx',
  '4xx': 'dashboard.statusBucket.4xx',
  '402': 'dashboard.statusColumn.402',
  '429': 'dashboard.statusBucket.429',
  '5xx': 'dashboard.statusBucket.5xx',
}

/** Tooltip copy for each TABLE column. Literal codes, so these say what the
 *  code means rather than what a class of codes means. */
export const COLUMN_DESCRIPTION_KEYS: Record<StatusColumn, TranslationKey> = {
  '200': 'dashboard.statusColumn.200',
  '400': 'dashboard.statusColumn.400',
  '401': 'dashboard.statusColumn.401',
  '402': 'dashboard.statusColumn.402',
  '403': 'dashboard.statusColumn.403',
  '404': 'dashboard.statusColumn.404',
  '405': 'dashboard.statusColumn.405',
  '429': 'dashboard.statusColumn.429',
  '5xx': 'dashboard.statusBucket.5xx',
}

/** The health state's name. `domain.*`, because it is product vocabulary. */
export const HEALTH_LABEL_KEYS: Record<HealthState, TranslationKey> = {
  down: 'domain.health.down',
  degraded: 'domain.health.degraded',
  healthy: 'domain.health.healthy',
  unknown: 'domain.health.unknown',
}

/** The sentence explaining what that state means. */
export const HEALTH_HINT_KEYS: Record<HealthState, TranslationKey> = {
  down: 'dashboard.healthHint.down',
  degraded: 'dashboard.healthHint.degraded',
  healthy: 'dashboard.healthHint.healthy',
  unknown: 'dashboard.healthHint.unknown',
}

export const numberFormat = new Intl.NumberFormat('en-US')

/**
 * 0.1234 -> "12.3%".
 *
 * A share that exists but rounds to zero at the requested precision is printed
 * as "<0.1%" (or "<1%" at zero digits) rather than as "0.0%", which would read
 * as "nothing here" for a real 5xx trickle — or, on the overview's health
 * tiles, put "0% of 1,062 endpoints" directly under a count of 4. A share that
 * is genuinely zero still prints as zero: that is a measurement, not a rounding
 * artefact.
 */
export function percent(ratio: number, digits = 1): string {
  // The smallest share this precision can express: 0.1% at one digit, 1% at
  // zero. Anything below it that is not actually zero says so as "<floor".
  const floor = Math.pow(10, -digits)
  if (ratio > 0 && ratio < floor / 100) return `<${floor.toFixed(digits)}%`
  return `${(ratio * 100).toFixed(digits)}%`
}
